import Board, {
  BOARD_DESKTOP_FRAME_OVERHEAD,
} from "../../chessBoard/components/Board";

import { Button } from "@/components/ui/button";
import { useSettings } from "@/redux/settings";
import { cn } from "@/lib/utils";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useRef, useState } from "react";
import { FaFastBackward } from "react-icons/fa";
import { FaStepBackward } from "react-icons/fa";
import { FaStepForward } from "react-icons/fa";
import { FaFastForward } from "react-icons/fa";
import MoveIcon from "@/components/common/move-icon";
import { ChessTerm, MoveType } from "@chesshotel/types";
import { convertMoves } from "@/util/moves";
import Comments from "../../modules/gameReview/components/Comments";
import { move2uci, uci2move } from "@chesshotel/gameReview";
import BlogBoardFrame, { blogBoardControlClass } from "./BlogBoardFrame";

const moveTypes = {
  [MoveType.BLUNDER]: { color: "redSquare", text: "Blunder" }, //ff4900
  [MoveType.MISTAKE]: { color: "purpleSqare", text: "Mistake" },
  [MoveType.INACCURACY]: { color: "orangeSquare", text: "Inaccuracy" },
  // [NEUTRAL]: { color: "blueSquare", text: "Neutral" },
  [MoveType.GOOD]: { color: "greenSquare", text: "Good" },
  [MoveType.EXCELLENT]: { color: "greenSquare", text: "Excellent" },
  [MoveType.BEST]: { color: "greenSquare", text: "Best" },
  [MoveType.GREAT]: { color: "greenSquare", text: "Great" },
  [MoveType.BRILLIANT]: { color: "greenSquare", text: "Brilliant" },
};

function isKeyMove(moveType: MoveType) {
  if (
    moveType == MoveType.BLUNDER ||
    // moveType == MoveType.INACCURACY ||
    moveType == MoveType.MISTAKE ||
    moveType == MoveType.GREAT ||
    moveType == MoveType.BRILLIANT
  ) {
    return true;
  }
  return false;
}

const startFen = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

//"k7/8/8/8/8/8/8/KR6 w - - 0 1";

//"r1b2rk1/ppp3pp/3b4/4Npq1/8/3B4/PPP2PPP/R2QR1K1 w - - 0 15";

// const startGameReviewPosition = {
//   fen: startFen,
//   move: "",
//   depth: 18,
//   score: 0.26,
//   expectedPoints: 0.52,
//   type: null,
//   bestMove: "",
//   pvMoves: [],
// };

export default function GameViewer({
  gameReview,
  playingWhite,
  title,
  caption,
  edgeColor,
}) {
  const startFen = gameReview ? gameReview[0].fen : null;

  const [positionIndex, setPositionIndex] = useState(0);

  // const [playingWhite, setPlayingWhite] = useState(false);
  const [possibleMoves, setPossibleMoves] = useState({});
  const [fen, setFen] = useState(startFen);
  const [animate, setAnimate] = useState(false);
  const [moves, setMoves] = useState([]);
  const [showBestMoves, setShowBestMoves] = useState(false);
  const [bestMoveIndex, setBestMoveIndex] = useState(1);

  const [chessTermArrows, setChessTermArrows] = useState(null);
  const [chessTermClick, setChessTermClick] = useState(false);

  const [highLightMove, setHighLightMove] = useState(false);

  const { t } = useTranslation("common");

  // const chess = useRef(new Chess()).current;

  const settings = useSettings();
  // const { boardSize } = settings;
  const boardSize = 450;

  const position = gameReview ? gameReview[positionIndex] : null;
  const lastPosition = gameReview ? gameReview[positionIndex - 1] : null;

  const bestMoveStr = lastPosition?.bestMove;
  const moveType = position?.type;

  const bestMove = bestMoveStr && uci2move(bestMoveStr);
  const lastMove = position?.move && uci2move(position?.move);

  const lastMoveColor = highLightMove ? null : moveTypes[moveType]?.color;

  useEffect(() => {
    const fen = gameReview && gameReview[positionIndex]?.fen;
    fen && setFen(fen);
  }, [position]);

  useEffect(() => {
    setChessTermArrows(null);
  }, [position]);

  useEffect(() => {
    setAnimate(true);
  }, []);

  if (!gameReview) {
    return <div>No game review yet</div>;
  }

  function handleChessTermOver(chessTerm: ChessTerm) {
    chessTerm.arrows && setChessTermArrows(chessTerm.arrows);
    setHighLightMove(true);
  }

  function handleChessTermOut(chessTerm: ChessTerm) {
    if (chessTermClick) {
      return;
    }
    setChessTermArrows(null);
    setHighLightMove(false);
  }

  function handleChessTermClick() {
    if (chessTermClick) {
      setChessTermArrows(null);
      setHighLightMove(false);
    }

    setChessTermClick(!chessTermClick);
  }

  function renderMoves() {
    const rows = [];
    let index = 1;

    if (!gameReview || gameReview.length <= 1) {
      return;
    }

    for (let i = 1; i < gameReview.length; i = i + 2) {
      const whitePos = gameReview[i];
      const blackPos = gameReview[i + 1];

      let whiteSelected = "";
      let whiteNotBestMove = "";

      if (i === positionIndex) {
        whiteSelected = "text-orange-400";
      }

      let blackSelected = "";
      let blackNotBestMove = "";

      if (i === positionIndex - 1) {
        blackSelected = "text-orange-400";
      }
      const whiteKeyMove = isKeyMove(whitePos.type);

      const whiteCol = (
        <td onClick={() => setPositionIndex(i)}>
          <div className={cn("flex gap-2", whiteSelected, whiteNotBestMove)}>
            {whiteKeyMove && <MoveIcon size="small" moveType={whitePos.type} />}
            <div className={!whiteKeyMove && cn("ml-3")}>{whitePos.move}</div>
          </div>
        </td>
      );

      const blackKeyMove = isKeyMove(whitePos.type);

      const blackCol = blackPos?.move ? (
        <td className="rounded-r-sm" onClick={() => setPositionIndex(i + 1)}>
          <div className={cn("flex gap-2", blackSelected, blackNotBestMove)}>
            {blackKeyMove && <MoveIcon size="small" moveType={blackPos.type} />}
            <div className={!blackKeyMove && cn("ml-3")}>{blackPos.move}</div>
          </div>
        </td>
      ) : (
        <td></td>
      );

      const keyId = `${i}${whitePos.move}${blackPos?.move}`;
      rows.push(
        <tr
          className="select-none even:bg-chess-ui-row [&>td]:h-9 [&>td]:transition-colors"
          key={keyId}
        >
          <td className="py-0.5 pl-3">{index}</td>
          {whiteCol}
          {blackCol}
        </tr>,
      );

      index++;
    }
    return rows;
  }

  async function moveMade(move) {
    // console.log("moveMade fen", fen);
    // const chess = Chess(fen);
    // const piece = chess.remove(move.from);
    // chess.put(piece, move.to);
    // chess.move(move);
    // const newFen = chess.fen();
    // const possibleMoves = convertMoves(chess.moves({ verbose: true }));
    // setPossibleMoves(possibleMoves);
    // const updatedMoves = [...moves, move];
    // setMoves(updatedMoves);
    // // const lastMove = moves[moves.length - 1];
    // await analyzePosition(newFen, move2uci(move));
    // setFen(newFen);
    // setPositionIndex(updatedMoves.length);
    // setPlayingWhite(chess.turn() === "w");
  }

  // const positions = [startFen, ...gameReview.map((position) => position.fen)];

  return (
    <BlogBoardFrame title={title} caption={caption}>
      <div className="flex bg-chess-ui-panel">
        <div className="flex justify-center bg-chess-ui-surface">
          <Board
            freeMove={true}
            fen={fen}
            animate={animate}
            size={boardSize}
            playingWhite={playingWhite}
            possibleMoves={possibleMoves}
            onMoveMade={(move) => {}}
            bestMove={bestMove}
            chessTermArrows={chessTermArrows}
            settings={settings}
            lastMove={lastMove}
            lastMoveColor={lastMoveColor}
            edgeColor={edgeColor}
            flushTop={Boolean(title)}
          />
        </div>
        <div
          className="flex flex-col items-stretch p-4 min-w-[260px] bg-chess-ui-panel"
          style={{ height: boardSize + BOARD_DESKTOP_FRAME_OVERHEAD + "px" }}
        >
          <div className="flex items-start w-full pb-4 mx-auto space-x-3">
            <Comments
              position={position}
              onChessTermOver={handleChessTermOver}
              onChessTermOut={handleChessTermOut}
              onChessTermClick={handleChessTermClick}
              isGuest={false}
              positionIndex={positionIndex}
            />
          </div>

          <div className="flex flex-col justify-between flex-grow overflow-auto">
            <table className="w-full my-0 text-sm border-collapse text-chess-ui-muted">
              <colgroup>
                <col style={{ width: "24%" }} />
                <col style={{ width: "38%" }} />
                <col style={{ width: "38%" }} />
              </colgroup>
              <tbody>{renderMoves()}</tbody>
            </table>
          </div>

          <div className="flex items-center justify-center gap-1 px-5 py-4">
            {/* <Button className="gap-2 mr-1">
            Next <FaKey />{" "}
          </Button> */}
            <Button
              variant="secondary"
              className={blogBoardControlClass}
              disabled={positionIndex === 0}
              onClick={() => setPositionIndex(0)}
            >
              <FaFastBackward />
            </Button>
            <Button
              variant="secondary"
              className={blogBoardControlClass}
              disabled={positionIndex === 0}
              onClick={() => {
                if (positionIndex > 0) setPositionIndex(positionIndex - 1);
              }}
            >
              <FaStepBackward />
            </Button>
            <Button
              variant="secondary"
              className={blogBoardControlClass}
              disabled={positionIndex === gameReview.length - 1}
              onClick={() => {
                if (positionIndex < gameReview.length - 1)
                  setPositionIndex(positionIndex + 1);
              }}
            >
              <FaStepForward />
            </Button>
            <Button
              className={blogBoardControlClass}
              variant="secondary"
              disabled={positionIndex === gameReview.length - 1}
              onClick={() => setPositionIndex(gameReview.length - 1)}
            >
              <FaFastForward />
            </Button>
          </div>
        </div>
      </div>
    </BlogBoardFrame>
  );
}
